import {
  useRef,
  useContext,
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  RefObject,
  ReactNode,
} from 'react';
import mitt from 'mitt';
import type { Emitter } from 'mitt';
import type { PCMEvent, RawPCMMonoAudioRecorder } from './RawPCMMonoAudioRecorderWorklet';
import {
  NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS,
  RawPCMMonoAudioRecorder as RawPCMMonoAudioRecorderWorklet,
} from './RawPCMMonoAudioRecorderWorklet';
import { DownsampleRawPCMMonoAudioRecorder as DownsampleRawPCMMonoAudioRecorderWorklet } from './DownsampleRawPCMMonoAudioRecorderWorklet';

import { useToasterDispatch } from 'common/ui/Toaster';
import Text from 'common/ui/Text';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { marks, measures } from 'domains/reporter/RichTextEditor/analytics/performance';
import { reporter } from 'modules/analytics/constants';
import analytics from 'modules/analytics';
import { performance } from 'utils/performance';
import { audioContextLazy, DEFAULT_EXPERIMENTAL_CONFIGURATION } from './AudioContextLazy';
import {
  getRecorderReadinessKey,
  hasRecordedForCaseAtom,
  recorderReadinessAtom,
  recorderState,
} from './state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useMicrophone } from 'domains/reporter/useMicrophone';
import { toMediaStreamDeviceId } from 'utils/dictaphone/products/virtual';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import type { BlobEvent } from 'types';
import { useReporterFeedbackToast } from 'hooks/useReporterFeedbackToast';
import { Editor, Range, RangeRef } from 'slate';
import { SlateMediaRecorder } from './SlateMediaRecorder';
import Deferred from 'utils/deferred';
import { reportStatusState, PROVISIONAL_SUBMIT_STATUSES } from 'domains/reporter/Reporter/state';
import type { ReportStatuses } from 'domains/reporter/Reporter/state';
import usePrevious from 'react-use/esm/usePrevious';
import useUnmount from 'react-use/esm/useUnmount';
import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';
import { getSelectionForDictation, partialEditor } from 'domains/reporter/RichTextEditor/utils';
import { useOnUnload } from 'hooks/useOnUnload';
import { useShouldSkipWebsocketCreation } from './useShouldSkipWebsocketCreation';
import { useAudioCueServiceSync } from 'hooks/useAudioCueServiceSync';
import { audioCueService } from 'modules/audioCueService';
import { createLockingFunction } from './utils';
import { useMostRecentInput, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { logger } from 'modules/logger';
import { useFocusMode } from 'hooks/useFocusMode';
import { useFocusModeRecordingRestart } from './useFocusModeRecordingRestart';
import { checkShouldUseASRPlex } from 'domains/reporter/RichTextEditor/plugins/dictation/utils';
import { useRetryASRPlexVADWebSocket } from 'domains/reporter/RichTextEditor/plugins/dictation/ASRPlex/useRetryASRPlexVADWebSocket';
import type { DownsampleRawPCMMonoAudioRecorder } from './DownsampleRawPCMMonoAudioRecorderWorklet';
import { env } from 'config/env';
import { asrPlexConfiguration } from 'domains/reporter/RichTextEditor/plugins/dictation/ASRPlex/ASRPlexDictation';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlate, useSlateSelection } from 'domains/reporter/RichTextEditor/core';
import Retry from 'utils/retry';

// https://github.com/twilio/twilio-video.js/issues/149#issuecomment-322545751
const isGetUserMediaSupported = () => {
  return !!navigator.mediaDevices?.getUserMedia;
};

export type RecorderEvent =
  | 'data_available'
  | 'stop_recording'
  | 'silence_detected'
  | 'modify_dictation';

export type RecorderDataAvailableEvent = {
  data: Nullable<Blob>;
  id: string;
  selectionRef?: RangeRef | null;
  duration: number;
  frameCount?: number;
  setShouldSkipWebsocketCreation: (arg1: boolean) => void;
  shouldSkipWebsocketCreation: boolean;
  shouldUseASRPlex: boolean;
};

export type RecorderModifyDictationEvent = {
  id: string;
  audioOffsetMs: number;
  localSequence: number;
};

export type PCMRecorderDataAvailableEvent = PCMEvent & {
  setShouldSkipWebsocketCreation: (arg1: boolean) => void;
  shouldSkipWebsocketCreation: boolean;
  shouldUseASRPlex: boolean;
};

export type RecorderStopRecordingEvent = {
  id: string;
  selection?: Range | null;
  duration: number;
  // When ASR Plex owns dictation, the NvoqQueue must ignore this event. Optional so existing
  // emitters (e.g. test drivers) remain valid; absence is treated as `false`.
  shouldUseASRPlex?: boolean;
};

export type RecorderSilenceDetectedEvent = RecorderStopRecordingEvent;

export type RecorderEventPayloadMap = {
  data_available: RecorderDataAvailableEvent | PCMRecorderDataAvailableEvent;
  stop_recording: RecorderStopRecordingEvent;
  silence_detected: RecorderSilenceDetectedEvent;
  modify_dictation: RecorderModifyDictationEvent;
};

export const isPCMRecorderDataAvailableEvent = (
  event: RecorderEventPayloadMap['data_available']
): event is PCMRecorderDataAvailableEvent => 'buffer' in event;

type RecorderEmitter = Emitter<RecorderEventPayloadMap>;

export const recorderEmitter: RecorderEmitter = mitt<
  Record<string, unknown>
>() as unknown as RecorderEmitter;

// nVoq's recommended period of silence before sending a boundary request
export const REQUEST_BOUNDARY_DELAY = 600;

// delay in ms between UI indication of recording stopping, and actual stopping under the hood
// see original implementation/explanation: https://github.com/SironaMedical/services-frontend/pull/3998
const RECORDING_DELAY_MS = 250;

// getUserMedia error names that indicate a transient failure (e.g. the mic is briefly held by
// another app/tab) rather than a permission problem, and are therefore worth retrying.
const RETRYABLE_GET_USER_MEDIA_ERRORS = new Set([
  'NotReadableError',
  'AbortError',
  'TrackStartError',
]);
const GET_USER_MEDIA_MAX_RETRIES = 2;
const GET_USER_MEDIA_RETRY_INITIAL_INTERVAL_MS = 500;

export type RecorderTarget =
  | 'reporter'
  | 'editMacroPreviewTab'
  | 'editMacroEditTab'
  | 'newMacro'
  | 'newFieldText'
  | 'newFieldPicklist'
  | 'templateEditor';

type RecorderContextProps = Readonly<{
  children: React.ReactNode;
}>;

export type RecorderContextBag = Readonly<{
  isRecording: boolean;
  showRecording: boolean;
  getRecorder: () =>
    | null
    | RawPCMMonoAudioRecorder
    | DownsampleRawPCMMonoAudioRecorder
    | SlateMediaRecorder;
  getAudioContext: () => AudioContext | null;
  getStream: () => null | MediaStream;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  restartRecording: () => Promise<void>;
  toggleRecording: () => void;
  setRecorderTarget: (arg1: ((arg1: RecorderTarget) => RecorderTarget) | RecorderTarget) => void;
  recorderTarget: RecorderTarget;
  userActivityElementRef: RefObject<HTMLDivElement>;
  sendModifyDictationMessage: () => void;
  setTextEditorElement: (el: HTMLDivElement | null) => void;
  setIsRecorderInitialized: Dispatch<SetStateAction<boolean>>;
  recorderVersion: number;
}>;

export const RecorderContext: React.Context<Nullable<RecorderContextBag>> =
  createContext<Nullable<RecorderContextBag>>(undefined);

const RecorderModifyDictationContext: React.Context<(() => void) | undefined> = createContext<
  (() => void) | undefined
>(undefined);

const getRecorderSelectionRef = (
  recorder: RawPCMMonoAudioRecorder | DownsampleRawPCMMonoAudioRecorder | SlateMediaRecorder | null
): RangeRef | null => {
  if (recorder == null) {
    return null;
  }

  return recorder.initialSelectionRef ?? null;
};

const RecorderProviderBase = ({ children }: RecorderContextProps): React.ReactElement => {
  const { enqueueToast } = useToasterDispatch();
  const startRecordingLock = useRef<Deferred<void> | null>(null);
  const stopRecordingLock = useRef<Deferred<void> | null>(null);
  const analyticsData = useWorklistItemAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const { audioInputDeviceId } = useMicrophone({ context: APOLLO_QUERY_CONTEXT });
  const [isRecording, setIsRecording] = useState(false);
  const [isRecorderInitialized, setIsRecorderInitialized] = useState(false);
  // Using a transaction instead of useRecoilValue because there seems to be a setPendingSelf
  // conflict that cancels out the atom value set, and not seeing the same behavior with useRecoilTransaction.
  const setIsDisplayingRecorderBorder = useRecoilCallback(
    ({ set }) =>
      (value: boolean) => {
        set(recorderState, value);
      },
    []
  );
  const showRecording = useRecoilValue(recorderState);
  const editor = useSlate();
  const editorSelection = useSlateSelection();
  const cleanStreamRef = useRef<MediaStream | null>(null);
  const [recorderTarget, setRecorderTarget] = useState<RecorderTarget>('reporter');
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const recorderRef = useRef<
    RawPCMMonoAudioRecorder | DownsampleRawPCMMonoAudioRecorder | SlateMediaRecorder | null
  >(null);
  const [recorderVersion, setRecorderVersion] = useState(0);
  const vadRecorderRef = useRef<RawPCMMonoAudioRecorder | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const resolveRecordingDelayRef = useRef<(() => void) | null>(null);
  const [asrPlexFeatureEnabled] = useFeatureFlagEnabled(FF.REPORTER_ASR_PLEX);
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const recordingDelay = RECORDING_DELAY_MS;

  const vadWebsocket = useRetryASRPlexVADWebSocket();
  const silenceDetectedRef = useRef<boolean>(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isMousePressed: mousePressedOnTextEditor, setElement: setTextEditorElement } =
    useMostRecentInput({ id: RICH_TEXT_EDITOR, context: APOLLO_QUERY_CONTEXT });
  const currentCaseId = useCurrentCaseId();
  const previousCaseId = usePrevious(currentCaseId);
  const setHasRecordedForCase = useSetRecoilState(hasRecordedForCaseAtom(currentCaseId ?? ''));

  // Capture the normalized base reporter route once at mount. Normalizing (vs the raw
  // pathname) keeps the atom key stable across in-reporter navigation so the viewer reads
  // the same key the reporter writes. The ref guards against key churn on re-render.
  const reporterPathname = useRef(getRecorderReadinessKey(window.location.pathname)).current;
  const setReadiness = useSetRecoilState(recorderReadinessAtom(reporterPathname));

  const [showRecorderNotReadyNotification, setShowRecorderNotReadyNotification] =
    useState<boolean>(false);

  useReporterFeedbackToast(showRecorderNotReadyNotification, {
    enableNotification: true,
    message: 'Audio could not be initialized, please refresh the page.',
    icon: 'redWarning',
  });

  const editorSelectionNestedRangeRef = useRef<RangeRef | null>(null);

  const getRecorder = useCallback(() => {
    return recorderRef.current;
  }, []);

  const {
    userActivityElementRef,
    shouldSkipWebsocketCreationRef,
    setShouldSkipWebsocketCreation,
    sendModifyDictationMessage,
  } = useShouldSkipWebsocketCreation({ isRecording, getRecorder, context: APOLLO_QUERY_CONTEXT });

  const { isFocusModeOn, isFocusModeSettingVisible } = useFocusMode({
    context: APOLLO_QUERY_CONTEXT,
  });
  const prevIsFocusModeOn = usePrevious(isFocusModeOn);

  const shouldUseASRPlex = checkShouldUseASRPlex({
    isFocusModeSettingVisible,
    isFocusModeOn,
    asrPlexFeatureEnabled,
  });

  useAudioCueServiceSync({ context: APOLLO_QUERY_CONTEXT });

  useEffect(() => {
    if (vadWebsocket == null) return;

    // If we detect 600ms of silence, set silence detected to true
    // We only want to do this once per window of silence
    // vad is short for voice activity detection, so:
    // vad = True: voice detected (silence NOT detected)
    // vad = False: silence detected (voice NOT detected)
    const onMessage = (event: MessageEvent) => {
      const message = event.data;
      if (message === 'False' && !silenceDetectedRef.current) {
        silenceTimerRef.current = setTimeout(() => {
          const recorder = recorderRef.current;
          if (!silenceDetectedRef.current && recorder != null) {
            recorderEmitter.emit('silence_detected', {
              id: recorder.id,
              selection: editorSelectionNestedRangeRef.current?.current,
              duration: recorder.getDurationMillis(),
              shouldUseASRPlex,
            });
          }
          silenceDetectedRef.current = true;
          silenceTimerRef.current = null;
        }, REQUEST_BOUNDARY_DELAY);
      } else if (message === 'True' && silenceDetectedRef.current) {
        const silenceTimer = silenceTimerRef.current;
        if (silenceTimer != null) {
          clearTimeout(silenceTimer);
        }
        silenceDetectedRef.current = false;
        silenceTimerRef.current = null;
      }
    };

    vadWebsocket.addEventListener('message', onMessage);
    return () => {
      vadWebsocket.removeEventListener('message', onMessage);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };
  }, [vadWebsocket, shouldUseASRPlex]);

  // After feature flags have loaded and audio context has initialized, we can start the media stream
  // The media stream will only be started once, and will be reused for all recordings
  useEffect(() => {
    if (isRecorderInitialized) return;

    const createMediaStream = async () => {
      let ac;

      try {
        ac = await audioContextLazy.get();
        if (audioContextRef.current == null) {
          audioContextRef.current = ac;
        }

        if (recorderRef.current) {
          // TODO: handle if recorder is already active
          logger.error('[createMediaStream] Cannot initialize; recorder is already initialized', {
            recorder: JSON.stringify(recorderRef.current),
          });
          return;
        }

        const stream = streamRef.current;
        if (stream) {
          logger.info('[createMediaStream] Stream already exists, stopping and removing it', {
            stream: JSON.stringify(stream),
          });
          const tracks: MediaStreamTrack[] = stream.getTracks() ?? [];
          tracks.forEach((track) => track.stop());
          streamRef.current = null;
        }

        // By default the echoCancellation, autoGainControl, and noiseSuppression are true (i.e. enabled).
        // Note that if you set echoCancellation to false but provide no values for the others, then they will be set to false (i.e. disabled).

        const getUserMediaConstraints: MediaStreamConstraints = {
          audio: {
            deviceId: { exact: toMediaStreamDeviceId(audioInputDeviceId) },
            echoCancellation: true,
            autoGainControl: true,
            noiseSuppression: true,
          },
        };

        const newStream = await navigator.mediaDevices
          ?.getUserMedia(getUserMediaConstraints)
          .catch(async (error: DOMException) => {
            if (env.STORYBOOK_STORYSHOTS === 'true') return;

            if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
              logger.warn(
                '[createMediaStream] Saved audio input device not found; useMicrophone will reset to default.',
                { audioInputDeviceId, errorName: error?.name }
              );
              return;
            }

            if (RETRYABLE_GET_USER_MEDIA_ERRORS.has(error?.name)) {
              logger.warn(
                '[createMediaStream] Transient getUserMedia failure; retrying with backoff.',
                { audioInputDeviceId, errorName: error?.name }
              );
              try {
                return await new Retry(
                  () => navigator.mediaDevices.getUserMedia(getUserMediaConstraints),
                  {
                    maxRetries: GET_USER_MEDIA_MAX_RETRIES,
                    initialInterval: GET_USER_MEDIA_RETRY_INITIAL_INTERVAL_MS,
                    backoffFn: (retries) => retries,
                  }
                ).retry();
              } catch (retryError) {
                logger.warn(
                  '[createMediaStream] Exhausted retries for transient getUserMedia failure.',
                  { audioInputDeviceId, errorName: error?.name, retryError }
                );
                setShowRecorderNotReadyNotification(true);
                return;
              }
            }

            enqueueToast(
              <>
                Please enable audio recording to enable dictation and other features within the
                Sirona Workspace.
                <br />
                You can do this at:
                <br />
                <code>chrome://settings/content/microphone</code>
                <br />
              </>,
              {
                severity: 'error',
                autoHideDuration: 10000,
                actionName: 'Copy Link',
                actionColor: 'info',
                actionHandler: () => {
                  navigator.clipboard.writeText('chrome://settings/content/microphone');
                },
              }
            );
            logger.warn(
              '[createMediaStream] Failed to get user media. Likely a user permission issue. Troubleshooting steps shown to user.',
              { errorName: error?.name }
            );
          });

        if (newStream == null) {
          logger.warn('[createMediaStream]: No audio stream available', {
            audioContext: JSON.stringify(ac),
            newStream: JSON.stringify(newStream),
          });
          return;
        }

        streamRef.current = newStream;
        setShowRecorderNotReadyNotification(false);
      } catch (error: any) {
        logger.error('[createMediaStream]: failed to create media stream', error);
        setShowRecorderNotReadyNotification(true);
      }
    };

    const init = async () => {
      await createMediaStream();
      // Only mark the recorder initialized once a stream was actually acquired.
      // Otherwise the effect's `if (isRecorderInitialized) return` guard would latch a
      // permanently-null recorder, and record presses would silently no-op until manual
      // mic reselection or reload. Leaving the flag false lets the effect retry when a
      // dep changes (e.g. useMicrophone resetting a stale device id to 'default').
      if (streamRef.current != null) {
        setIsRecorderInitialized(true);
      }
    };
    init();
  }, [audioInputDeviceId, enqueueToast, isRecorderInitialized]);

  useEffect(() => {
    const hasStream = streamRef.current != null;
    setReadiness(isRecorderInitialized && hasStream);
  }, [isRecorderInitialized, setReadiness]);

  useUnmount(() => {
    setReadiness(false);
  });

  const stopRecording = useCallback(
    async ({ force = false } = {}) => {
      if (stopRecordingLock.current != null) {
        // A stop is already in progress; resolve any pending delay and wait for it to finish.
        if (resolveRecordingDelayRef.current != null) {
          resolveRecordingDelayRef.current();
        }
        await stopRecordingLock.current.promise;
        return;
      }

      if (startRecordingLock.current != null) {
        await startRecordingLock.current.promise;
      } else if (isRecording) {
        logger.warn('[useRecorder] startRecordingLock expected to exist in stopRecording callback');
      }
      stopRecordingLock.current = new Deferred();

      setIsDisplayingRecorderBorder(false);

      audioCueService.playStop();

      if (!force && recordingDelay > 0) {
        // A user's hand action to toggle recording may not be perfectly timed with their speech.
        // By adding a 250ms delay, we will catch any trailing audio that would otherwise be cut off.
        const { promise: recordingDelayPromise, trigger: resolveRecordingDelay } =
          createLockingFunction(recordingDelay);
        resolveRecordingDelayRef.current = resolveRecordingDelay;
        await recordingDelayPromise;
        resolveRecordingDelayRef.current = null;
      }

      const recorder = recorderRef.current;
      const vadRecorder = vadRecorderRef.current;

      const resolveStopRecordingLock = () => {
        if (stopRecordingLock.current?.resolve != null) {
          stopRecordingLock.current.resolve();
        }
        stopRecordingLock.current = null;
      };

      const onStop = () => {
        performance.mark(marks.sirona.Dictation.RecordingStopped);
        analytics.track(reporter.usr.recordingStopped, {
          selection: editorSelection != null ? stringifyRange(editorSelection) : 'null',
        });
        performance.measure(
          measures.sirona.Dictation.DictationDuration.name,
          measures.sirona.Dictation.DictationDuration.startMark,
          measures.sirona.Dictation.DictationDuration.endMark
        );
        performance.clearMarksByName([
          marks.sirona.Dictation.RecordingStarted,
          marks.sirona.Dictation.RecordingStopped,
        ]);

        // NOTE: This ensures is recording is not set to false until the recorder has done its cleanup
        // otherwise audio frames could be dropped resulting in transcription inaccuracies
        setIsRecording(false);
        resolveStopRecordingLock();
      };
      if (vadRecorder != null) {
        vadRecorder.stopRecording();
      }
      // Only wait for onStop when the recorder is genuinely still recording — the PCM
      // worklets early-return (and SlateMediaRecorder throws) when already stopped, so
      // onStop would never fire and the stop lock would stay pending forever.
      if (
        recorder != null &&
        ('recording' in recorder ? recorder.recording : recorder.state === 'recording')
      ) {
        recorder.stopRecording(onStop);
      } else {
        setIsRecording(false);
        resolveStopRecordingLock();
      }

      // Disconnect all the audio nodes in the audio graph
      sourceRef.current?.disconnect();
      sourceRef.current = null;
      recorderRef.current = null;
      vadRecorderRef.current = null;
      destinationRef.current?.disconnect();
      destinationRef.current = null;
      cleanStreamRef.current = null;
    },
    [isRecording, setIsDisplayingRecorderBorder, recordingDelay, editorSelection]
  );

  // Effect to create new recorder when selection changes
  useEffect(() => {
    if (!isRecording) return;
    if (stopRecordingLock.current != null) {
      return;
    }
    if (previousCaseId !== currentCaseId) {
      stopRecording();
      return;
    }
    // If focus mode is toggled while ASR Plex is enabled, let the restart effect handle stop + start.
    // Otherwise keep the legacy behavior:
    // This is because we need to cleanly teardown and build different pcm and webm recorders.
    // When PCM recorder is used exclusively, we can revisit.
    if (isFocusModeOn !== prevIsFocusModeOn && prevIsFocusModeOn != null) {
      if (asrPlexFeatureEnabled) {
        return;
      }
      stopRecording();
      return;
    }

    // If the mouse is pressed and a recorder was already started, don't create another.
    if (mousePressedOnTextEditor && recorderRef.current != null) {
      return;
    }

    // The dictation selection is used to account for potential differences between the user's real selection in the editor
    // e.g. with a collapsed dictation in the middle of a word - we intend to have the dictation selection at end of word instead
    let dictationSelectionRef: RangeRef | null = null;
    if (editor != null) {
      const selectionForDictation = getSelectionForDictation(editor) ?? editorSelection;
      if (selectionForDictation != null) {
        dictationSelectionRef = Editor.rangeRef(editor, selectionForDictation);
      }
    }

    editorSelectionNestedRangeRef.current =
      editor != null && editorSelection != null ? Editor.rangeRef(editor, editorSelection) : null;

    // The selection should never be null, this would be a failure mode where the user is
    // dictating and we don't know where to place the dictation in the document.
    if (
      !isFocusModeOn &&
      dictationSelectionRef?.current == null &&
      !PROVISIONAL_SUBMIT_STATUSES.includes(
        reportStatus as 'provisionalReportSubmit' | 'provisionalAddendumSubmit'
      )
    ) {
      const warningMessage = '[useRecorder] Recording without a selection, skipping';
      logger.warn(warningMessage, {
        reportStatus,
        editor: editor != null ? partialEditor(editor) : 'null',
      });
      return;
    }

    const cleanStream = cleanStreamRef.current;
    if (cleanStream == null) return;

    const currentRecorder = recorderRef.current;
    const currentSelectionRef = getRecorderSelectionRef(currentRecorder);
    if (currentRecorder != null && isFocusModeOn) return;
    if (
      currentRecorder != null &&
      currentSelectionRef?.current != null &&
      dictationSelectionRef?.current != null &&
      Range.equals(currentSelectionRef.current, dictationSelectionRef.current)
    ) {
      // selections have not changed, so do not stop and start a new recorder at the same selection
      return;
    }

    if (shouldSkipWebsocketCreationRef.current === true) return;

    if (currentRecorder != null) {
      currentRecorder.stopRecording(() => {
        recorderEmitter.emit('stop_recording', {
          id: currentRecorder.id,
          selection: currentSelectionRef?.current ?? null,
          duration: currentRecorder.getDurationMillis(),
          shouldUseASRPlex,
        });
      });
    }

    let newRecorder;

    if (shouldUseASRPlex) {
      // Create a PCM recorder when using ASR Plex or when explicitly not using WebM.
      logger.info(`[useRecorder] Creating PCM recorder for ASRPlexDictation`);
      const ac = audioContextRef.current;
      if (ac == null) return;
      type RecorderWorkletEvent = Omit<PCMEvent, 'initialSelectionRef'> & {
        initialSelectionRef?: PCMEvent['initialSelectionRef'];
      };
      const initialSelectionRef = dictationSelectionRef ?? undefined;
      const workletOptions = {
        onDataAvailable: (ev: RecorderWorkletEvent) => {
          const normalizedEvent: PCMEvent = {
            ...ev,
            initialSelectionRef: ev.initialSelectionRef ?? null,
          };
          recorderEmitter.emit('data_available', {
            ...normalizedEvent,
            setShouldSkipWebsocketCreation,
            shouldSkipWebsocketCreation: shouldSkipWebsocketCreationRef.current,
            shouldUseASRPlex,
          });
        },
        analytics: analyticsData,
        sourceSampleRate: shouldUseASRPlex
          ? asrPlexConfiguration.audioSamplingRate
          : DEFAULT_EXPERIMENTAL_CONFIGURATION.audioSamplingRate,
        initialSelectionRef,
      } as const;
      if (shouldUseASRPlex) {
        newRecorder = new RawPCMMonoAudioRecorderWorklet(cleanStream, ac, workletOptions);
      } else {
        newRecorder = new DownsampleRawPCMMonoAudioRecorderWorklet(cleanStream, ac, {
          targetSampleRate: 16000,
          ...workletOptions,
        });
      }
      newRecorder.startRecording();
    } else {
      logger.info('[useRecorder] Creating WebM recorder for useNvoqQueue');
      if (dictationSelectionRef == null) {
        logger.warn('[useRecorder] Unable to create SlateMediaRecorder without a selection');
        return;
      }
      newRecorder = new SlateMediaRecorder(cleanStream, {
        mimeType: 'audio/webm',
        initialSelectionRef: dictationSelectionRef,
      });

      newRecorder.addEventListener('dataavailable', (ev: BlobEvent) => {
        recorderEmitter.emit('data_available', {
          data: ev.data,
          id: (ev.target as SlateMediaRecorder).id,
          selectionRef: (ev.target as SlateMediaRecorder).initialSelectionRef,
          duration: (ev.target as SlateMediaRecorder).getDurationMillis(),
          setShouldSkipWebsocketCreation,
          shouldSkipWebsocketCreation: shouldSkipWebsocketCreationRef.current,
          shouldUseASRPlex,
        });
      });
      newRecorder.startRecording(NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS);
    }
    recorderRef.current = newRecorder;
    setRecorderVersion((prev) => prev + 1);
  }, [
    isFocusModeOn,
    isRecording,
    reportStatus,
    editorSelection,
    editor,
    shouldUseASRPlex,
    shouldSkipWebsocketCreationRef,
    setShouldSkipWebsocketCreation,
    mousePressedOnTextEditor,
    previousCaseId,
    currentCaseId,
    stopRecording,
    analyticsData,
    prevIsFocusModeOn,
    asrPlexFeatureEnabled,
  ]);

  /* NOTE ON THE AUDIO GRAPH
   * This method bootstraps an audio graph, which looks like:
   *
   * (Mic) MediaStreamSource => (Placeholder) MediaStreamDestination
   *
   * (Placeholder) MediaStreamSource => (Downsampler) AudioWorklet => (Output) MediaDestination
   *
   * We end up creating two disconnected graphs by making a new stream source from the placeholder destination.
   *
   * TODO: This graph can be simpler.
   */
  const startRecording = useCallback(async () => {
    startRecordingLock.current = new Deferred();

    // If we are currently stopping, wait for the stop to finish before starting again.
    if (stopRecordingLock.current != null) {
      if (resolveRecordingDelayRef.current != null) {
        resolveRecordingDelayRef.current();
      }
      await stopRecordingLock.current.promise;
    }

    const start = async () => {
      // This is to ensure that a websocket is always created when the user starts a new recording.
      // For context, we previously tried setShouldSkipWebsocketCreation(false) during stopRecording
      // However stableText can still be processed after stopRecording, where setShouldSkipWebsocketCreation(true) may be triggered,
      // and thus preventing new websockets from being created
      setShouldSkipWebsocketCreation(false);

      if (!isGetUserMediaSupported()) {
        enqueueToast(
          <>
            Please access the Sirona Workspace through the{' '}
            <Text
              as="a"
              href="https://www.google.com/chrome/"
              target="_blank"
              rel="noopener noreferrer"
            >
              latest version of Google Chrome
            </Text>{' '}
            to enable dictation and other voice-driven features within the Sirona Workspace.
          </>,
          { severity: 'error' }
        );
        return;
      }
      if (recorderRef.current) {
        // TODO: handle if recorder is already active
        const activeErrorMessage =
          '[useRecorder] Cannot initialize; recorderRef worklet is already initialized';
        logger.error(activeErrorMessage, {
          recorder: JSON.stringify(recorderRef.current),
        });
        return;
      }

      const ac = audioContextRef.current;
      const mediaStream = streamRef.current;

      if (ac == null || mediaStream == null) {
        logger.error('[useRecorder] Audio context or media stream is null during startRecording', {
          audioContext: JSON.stringify(ac),
          mediaStream: JSON.stringify(mediaStream),
        });
        return;
      }

      const source = ac.createMediaStreamSource(mediaStream);
      sourceRef.current = source;

      const destination = ac.createMediaStreamDestination();
      destinationRef.current = destination;
      source.connect(destination);

      const cleanStream = destination.stream;
      cleanStreamRef.current = cleanStream;

      // Create a vadRecorder. This isn't being handled in the above effects because
      // we only need to create a single connection per dictation (rather than per selection).
      if (vadRecorderRef.current == null && shouldUseASRPlex === false) {
        const onDataAvailable = ({ data }: PCMEvent) => {
          if (vadWebsocket == null) {
            logger.error(
              '[useRecorder] vadWebsocket is null during onDataAvailable callback',
              vadWebsocket
            );
            return;
          }
          vadWebsocket.send(data);
        };

        const vadRecorder = new RawPCMMonoAudioRecorderWorklet(cleanStream, ac, {
          onDataAvailable,
          sourceSampleRate: asrPlexConfiguration.audioSamplingRate,
          initialSelectionRef: editorSelectionNestedRangeRef.current,
        });
        vadRecorderRef.current = vadRecorder;
        await vadRecorder.onRecorderReady;
        vadRecorder.startRecording();
        vadRecorderRef.current = vadRecorder;
      }

      performance.mark(marks.sirona.Dictation.RecordingStarted);
      audioCueService.playStart();
      analytics.track(reporter.usr.recordingStarted, {
        selection:
          editorSelectionNestedRangeRef.current?.current != null
            ? stringifyRange(editorSelectionNestedRangeRef.current?.current)
            : 'null',
      });
      if (currentCaseId) {
        setHasRecordedForCase(true);
      }

      streamRef.current = mediaStream;

      setIsRecording(true);
      setIsDisplayingRecorderBorder(true);
    };

    try {
      await start();
    } catch (e: any) {
      const startErrorMessage = '[useRecorder] Executing start() threw an error';
      logger.error(startErrorMessage, e, {
        selection:
          editorSelectionNestedRangeRef.current?.current != null
            ? stringifyRange(editorSelectionNestedRangeRef.current.current)
            : 'null',
      });
    } finally {
      // always remove the lock after start returns or throws
      if (startRecordingLock.current?.resolve != null) {
        startRecordingLock.current.resolve();
      } else {
        const recordingLockError =
          '[useRecorder] Cannot resolve recording lock - startRecordingLock expected to exist in startRecording callback';
        logger.error(recordingLockError);
        // eslint-disable-next-line no-unsafe-finally
        return;
      }
    }
  }, [
    setShouldSkipWebsocketCreation,
    shouldUseASRPlex,
    currentCaseId,
    setIsDisplayingRecorderBorder,
    enqueueToast,
    vadWebsocket,
    setHasRecordedForCase,
  ]);

  const cleanUpRecording = useCallback(() => {
    if (isRecording) {
      stopRecording({ force: true });
    }
  }, [isRecording, stopRecording]);

  useUnmount(() => {
    cleanUpRecording();
  });

  useOnUnload(cleanUpRecording);

  const restartRecording = useCallback(async () => {
    await stopRecording();

    // Wait until stopRecordingLock is done
    if (stopRecordingLock.current?.promise) {
      await stopRecordingLock.current.promise;
    }

    // Wait one tick to let React flush setState (important!)
    await new Promise((r) => setTimeout(r, 0));

    await startRecording();
  }, [stopRecording, startRecording]);

  useFocusModeRecordingRestart({
    isRecording,
    isFocusModeOn,
    prevIsFocusModeOn,
    asrPlexFeatureEnabled,
    stopRecording,
    startRecording,
  });

  const toggleRecording = useCallback(() => {
    if (isRecording && resolveRecordingDelayRef.current == null) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  const getStream = useCallback(() => {
    return streamRef.current;
  }, []);

  const getAudioContext = useCallback(() => {
    return audioContextRef.current;
  }, []);

  const recorderBag = useMemo(
    () => ({
      isRecording,
      showRecording,
      startRecording,
      recorderTarget,
      setRecorderTarget,
      stopRecording,
      restartRecording,
      toggleRecording,
      getRecorder,
      getStream,
      getAudioContext,
      userActivityElementRef,
      sendModifyDictationMessage,
      setTextEditorElement,
      setIsRecorderInitialized,
      recorderVersion,
    }),
    [
      isRecording,
      showRecording,
      startRecording,
      recorderTarget,
      setRecorderTarget,
      stopRecording,
      restartRecording,
      toggleRecording,
      getRecorder,
      getStream,
      getAudioContext,
      userActivityElementRef,
      sendModifyDictationMessage,
      setTextEditorElement,
      setIsRecorderInitialized,
      recorderVersion,
    ]
  );

  return (
    <RecorderModifyDictationContext.Provider value={sendModifyDictationMessage}>
      <RecorderContext.Provider value={recorderBag}>{children}</RecorderContext.Provider>
    </RecorderModifyDictationContext.Provider>
  );
};

export const RecorderProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const recorder = useContext(RecorderContext);

  // If a provider already exists up the tree, reuse it so nested providers share the same state.
  if (recorder != null) {
    return <>{children}</>;
  }

  return <RecorderProviderBase>{children}</RecorderProviderBase>;
};

RecorderProvider.displayName = 'RecorderProvider';

export const useRecorder = (): RecorderContextBag => {
  const recorder = useContext(RecorderContext);

  if (!recorder) {
    throw new Error('[useRecorder] Hook must be used within a RecorderProvider.');
  }

  return recorder;
};

export const useSendModifyDictationMessage = (): (() => void) => {
  const sendModifyDictationMessage = useContext(RecorderModifyDictationContext);

  if (sendModifyDictationMessage == null) {
    throw new Error('[useSendModifyDictationMessage] Hook must be used within a RecorderProvider.');
  }

  return sendModifyDictationMessage;
};

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RecorderProvider);
